import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { useFormik } from 'formik';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { Button } from '@/components/Button/Button';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { FormattedResponseType, getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { PERSONAL_DETAILS_VALIDATION_SCHEMA, PersonalDetailsFormType } from '@/const/formConfigs';
import { getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { useMutation } from '@tanstack/react-query';
import { editInternalOrganizationUser } from '../api/api';
import { InternalOrganizationUserType } from '../../types/types';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { editSelfUserDetails } from '@/bundle/shared/api';
import { SelfUserDetailsType } from '@/api/v1/users/getSelfUserDetailsApi';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { PERSONAL_INFORMATION_UPDATED_MESSAGE } from '@/const/shared';

type EditPersonalDetailsFormProps = {
  user: InternalOrganizationUserType;
  isOwnAccount: boolean;
  onSuccess: () => void;
  onClose: () => void;
};

export const EditAdminPersonalDetailsForm = ({
  user,
  isOwnAccount,
  onSuccess,
  onClose,
}: EditPersonalDetailsFormProps) => {
  const { first_name, last_name, middle_name, id } = user;

  const formik = useFormik({
    initialValues: {
      first_name,
      middle_name,
      last_name,
    },
    validationSchema: PERSONAL_DETAILS_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      const payload = getChangedFormValues<PersonalDetailsFormType>(values, formik.initialValues);

      if (!payload) {
        return onClose();
      }

      /* We have restriction in backend for edit self user details:
        - admin updating other user -> use endpoint for update organization user by id
        - admin updating himself -> use endpoint for self update
      */
      if (isOwnAccount) {
        editSelfUserMutate(payload);

        return;
      }

      editInternalUserMutate(payload);
    },
  });

  const handleResponse = (response: FormattedResponseType<SelfUserDetailsType | InternalOrganizationUserType>) => {
    if (response?.error) {
      return showSnackbarResponseError(response?.error);
    }

    showSnackbar(PERSONAL_INFORMATION_UPDATED_MESSAGE);
    onClose();
    onSuccess();
  };

  const {
    data: editSelfUserData,
    mutate: editSelfUserMutate,
    isPending: isPendingEditSelfUser,
  } = useMutation({
    mutationKey: ['edit_self_user_details'],
    mutationFn: (values: PersonalDetailsFormType) => {
      return editSelfUserDetails(values);
    },
    onSuccess(response) {
      handleResponse(response);
    },
  });

  const {
    data: editInternalUserData,
    mutate: editInternalUserMutate,
    isPending: isPendingEditInternalUser,
  } = useMutation({
    mutationKey: ['edit_internal_user_details'],
    mutationFn: (values: PersonalDetailsFormType) => {
      return editInternalOrganizationUser(id, values);
    },
    onSuccess(response) {
      handleResponse(response);
    },
  });

  const apiError = editInternalUserData?.error || editSelfUserData?.error;
  const isLoading = isPendingEditSelfUser || isPendingEditInternalUser;
  const formError = getResponseError(apiError);

  return (
    <FormikForm value={formik} error={formError}>
      <FormikInput name='first_name' label='First Name*' placeholder='Enter First Name' apiError={apiError} />
      <FormikInput name='middle_name' label='Middle Name' placeholder='Enter Middle Name' apiError={apiError} />
      <FormikInput name='last_name' label='Last Name*' placeholder='Enter Last Name' apiError={apiError} />
      <ButtonGroup marginTop='12px'>
        <Button type='button' variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button type='submit' isLoading={isLoading}>
          Save
        </Button>
      </ButtonGroup>
    </FormikForm>
  );
};
